import api from "../api";
import type { MasterBriefDto } from "../models/MasterBrief";

type ApiResponse<T> = { statusCode: number; message?: string; data?: T };
type ApiErrorResponse = {
  statusCode?: number;
  message?: string;
  data?: null;
  error?: string | null;
};

export type PartsCatalogZipDownload = {
  blob: Blob;
  fileName: string;
  contentType: string;
};

export async function fetchMasterBriefs(oem: string, vendorId: number): Promise<MasterBriefDto[]> {
  // Adjust the path if your controller uses a different route
  const { data } = await api.get<ApiResponse<MasterBriefDto[]>>(`/Parts/components/all/${oem}/${vendorId}`);
  if (data?.statusCode === 200 && Array.isArray(data.data)) return data.data;
  return [];
}

function getFilenameFromDisposition(contentDisposition: string | null): string | null {
  if (!contentDisposition) return null;

  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1].replace(/"/g, "").trim());
    } catch {
      // fall through to standard filename parser
    }
  }

  const asciiMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
  return asciiMatch?.[1]?.trim() || null;
}

async function toApiErrorMessage(errorBlob: Blob, status: number): Promise<string> {
  try {
    const text = await errorBlob.text();
    if (!text) return `Failed to generate parts catalog PDF package (${status}).`;

    const parsed = JSON.parse(text) as ApiErrorResponse;
    return (
      parsed?.message ||
      parsed?.error ||
      `Failed to generate parts catalog PDF package (${status}).`
    );
  } catch {
    return `Failed to generate parts catalog PDF package (${status}).`;
  }
}

export async function generatePartCatalogPdfZip(
  oem: string,
  masterAssemblyId: number,
  languageCode?: string
): Promise<PartsCatalogZipDownload> {
  if (!oem?.trim()) {
    throw new Error("OEM is required.");
  }
  if (!masterAssemblyId || Number.isNaN(masterAssemblyId)) {
    throw new Error("Valid master assembly id is required.");
  }

  const normalizedOem = oem.trim();
  const normalizedLang = String(languageCode || "").trim().toLowerCase();
  const query = normalizedLang ? `?lang=${encodeURIComponent(normalizedLang)}` : "";
  const endpoint = `/Parts/partcatalog-pdf/${encodeURIComponent(normalizedOem)}/${masterAssemblyId}${query}`;

  const response = await api.post<Blob>(endpoint, undefined, {
    responseType: "blob",
    headers: { Accept: "application/zip" },
    validateStatus: () => true,
  });

  if (response.status < 200 || response.status >= 300) {
    const errorMessage = await toApiErrorMessage(response.data, response.status);
    throw new Error(errorMessage);
  }

  const contentDisposition = response.headers?.["content-disposition"] || null;
  const filenameFromHeader = getFilenameFromDisposition(contentDisposition);
  const langSuffix = normalizedLang ? `-${normalizedLang}` : "";
  const fallbackFileName = `partcatalog-${normalizedOem.toLowerCase()}-${masterAssemblyId}${langSuffix}.zip`;

  return {
    blob: response.data,
    fileName: filenameFromHeader || fallbackFileName,
    contentType: response.headers?.["content-type"] || "application/zip",
  };
}
